import type { ClientLocation } from './ranking.js';

// Ported from prototype/src/data/seed.js (LOCATIONS) and prototype/src/agent.js (detectLocation).
// Astrologers type their city freely, clients type places freely, and bids target a country or a
// city — so every comparison goes through canonicalCity / canonicalCountry to absorb the common
// spellings ("Bangalore" = "Bengaluru", "Gurgaon" = "Delhi NCR", "USA" = "United States").

export const ONLINE_LABEL = 'Worldwide (online consultations)';
export const ONLINE_QUICK_REPLY = 'Online is fine';

export const LOCATIONS = [
  ONLINE_LABEL,
  'India',
  'India — Delhi NCR',
  'India — Mumbai',
  'India — Bengaluru',
  'India — Chennai',
  'India — Hyderabad',
  'India — Kolkata',
  'India — Pune',
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'United Arab Emirates',
  'Singapore',
  'New Zealand',
];

// The chips the Rishi offers when it needs a location (prototype Home.jsx).
export const LOCATION_QUICK_REPLIES = [
  ONLINE_QUICK_REPLY,
  'India',
  'India — Delhi NCR',
  'India — Mumbai',
  'India — Bengaluru',
  'United States',
  'United Kingdom',
  'Australia',
  'Canada',
];

// "Change location" offers online plus the first eight concrete places (prototype Home.jsx).
export const CHANGE_LOCATION_QUICK_REPLIES = [ONLINE_QUICK_REPLY, ...LOCATIONS.slice(1, 9)];

export const COUNTRIES = [
  'India',
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'United Arab Emirates',
  'Singapore',
  'New Zealand',
  'Nepal',
  'Sri Lanka',
  'Other',
];

const CITY_ALIASES: Record<string, string> = {
  bangalore: 'bengaluru',
  bengaluru: 'bengaluru',
  delhi: 'delhi ncr',
  'new delhi': 'delhi ncr',
  'delhi ncr': 'delhi ncr',
  ncr: 'delhi ncr',
  noida: 'delhi ncr',
  gurgaon: 'delhi ncr',
  gurugram: 'delhi ncr',
  ghaziabad: 'delhi ncr',
  faridabad: 'delhi ncr',
  bombay: 'mumbai',
  mumbai: 'mumbai',
  'navi mumbai': 'mumbai',
  thane: 'mumbai',
  madras: 'chennai',
  chennai: 'chennai',
  calcutta: 'kolkata',
  kolkata: 'kolkata',
  poona: 'pune',
  pune: 'pune',
  hyderabad: 'hyderabad',
  secunderabad: 'hyderabad',
  // Devanagari (LANE-D-3)
  दिल्ली: 'delhi ncr',
  'नई दिल्ली': 'delhi ncr',
  मुंबई: 'mumbai',
  बेंगलुरु: 'bengaluru',
  बैंगलोर: 'bengaluru',
  पुणे: 'pune',
  कोलकाता: 'kolkata',
  चेन्नई: 'chennai',
  हैदराबाद: 'hyderabad',
};

const COUNTRY_ALIASES: Record<string, string> = {
  india: 'india',
  bharat: 'india',
  // No bare "us" alias: it would turn "help us" into a location.
  usa: 'united states',
  america: 'united states',
  'united states': 'united states',
  'united states of america': 'united states',
  uk: 'united kingdom',
  england: 'united kingdom',
  britain: 'united kingdom',
  'great britain': 'united kingdom',
  'united kingdom': 'united kingdom',
  uae: 'united arab emirates',
  emirates: 'united arab emirates',
  'united arab emirates': 'united arab emirates',
  canada: 'canada',
  australia: 'australia',
  singapore: 'singapore',
  'new zealand': 'new zealand',
  nz: 'new zealand',
  nepal: 'nepal',
  'sri lanka': 'sri lanka',
};

// Well-known cities we can place in a country, so "I live in London" also matches UK bids.
const CITY_COUNTRY: Record<string, string> = {
  'delhi ncr': 'India',
  mumbai: 'India',
  bengaluru: 'India',
  chennai: 'India',
  hyderabad: 'India',
  kolkata: 'India',
  pune: 'India',
  jaipur: 'India',
  ahmedabad: 'India',
  lucknow: 'India',
  varanasi: 'India',
  chandigarh: 'India',
  kochi: 'India',
  indore: 'India',
  london: 'United Kingdom',
  manchester: 'United Kingdom',
  birmingham: 'United Kingdom',
  leicester: 'United Kingdom',
  toronto: 'Canada',
  vancouver: 'Canada',
  brampton: 'Canada',
  sydney: 'Australia',
  melbourne: 'Australia',
  dubai: 'United Arab Emirates',
  'abu dhabi': 'United Arab Emirates',
  'new york': 'United States',
  'new jersey': 'United States',
  chicago: 'United States',
  houston: 'United States',
  'san francisco': 'United States',
  auckland: 'New Zealand',
  kathmandu: 'Nepal',
  colombo: 'Sri Lanka',
};

// Display spelling for canonical city keys that differ from a simple capitalisation.
const CITY_DISPLAY: Record<string, string> = {
  'delhi ncr': 'Delhi NCR',
};

function clean(s: string | null | undefined): string {
  return (s ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[,;]+$/, '');
}

export function canonicalCity(s: string | null | undefined): string {
  const c = clean(s);
  return Object.hasOwn(CITY_ALIASES, c) ? (CITY_ALIASES[c] ?? c) : c;
}

export function canonicalCountry(s: string | null | undefined): string {
  const c = clean(s);
  return Object.hasOwn(COUNTRY_ALIASES, c) ? (COUNTRY_ALIASES[c] ?? c) : c;
}

export function samePlace(
  a: string | null | undefined,
  b: string | null | undefined,
  kind: 'city' | 'country',
): boolean {
  const ca = kind === 'city' ? canonicalCity(a) : canonicalCountry(a);
  const cb = kind === 'city' ? canonicalCity(b) : canonicalCountry(b);
  return ca !== '' && ca === cb;
}

export function countryForCity(city: string | null | undefined): string | undefined {
  const c = canonicalCity(city);
  return Object.hasOwn(CITY_COUNTRY, c) ? CITY_COUNTRY[c] : undefined;
}

function titleCase(s: string): string {
  return s.replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

export function displayCity(city: string): string {
  const c = canonicalCity(city);
  if (Object.hasOwn(CITY_DISPLAY, c)) return CITY_DISPLAY[c] ?? city;
  return titleCase(c);
}

// "India — Mumbai" → { online: false, country: 'India', city: 'Mumbai' }; "Online is fine" and the
// worldwide label → { online: true }; a bare country → { country }. Returns null for anything that
// isn't one of these shapes (free text is handed to extraction instead).
export function parseLocationChoice(label: string): ClientLocation | null {
  const t = label.trim();
  if (!t) return null;
  if (t === ONLINE_QUICK_REPLY || t === ONLINE_LABEL) return { online: true };
  const parts = t.split(' — ');
  if (parts.length === 2 && parts[0] && parts[1]) {
    return { online: false, country: parts[0].trim(), city: parts[1].trim() };
  }
  if (LOCATIONS.includes(t) || COUNTRIES.includes(t)) return { online: false, country: t };
  return null;
}

export function locationLabel(loc: ClientLocation | null | undefined): string {
  if (!loc || loc.online) return ONLINE_LABEL;
  if (loc.city && loc.country) return `${loc.country} — ${loc.city}`;
  return loc.city ?? loc.country ?? ONLINE_LABEL;
}

const ONLINE_PATTERN = /\b(online|anywhere|doesn.?t matter|any location|remote|video call)\b/;

// Deterministic location detection: online intent first, then cities (more specific, including
// aliases), then countries. `knownCities`/`knownCountries` are the places our astrologers use, so a
// city that only exists in our data is still recognised.
export function detectLocation(
  text: string,
  knownCities: string[] = [],
  knownCountries: string[] = [],
): ClientLocation | null {
  const t = ` ${clean(text).replace(/[.!?,;:।]/g, ' ')} `;
  if (ONLINE_PATTERN.test(t)) return { online: true };

  const cityCandidates = [
    ...Object.keys(CITY_ALIASES),
    ...Object.keys(CITY_COUNTRY),
    ...knownCities,
  ]
    .map(clean)
    .filter((c) => c.length > 1)
    .sort((a, b) => b.length - a.length);
  for (const c of cityCandidates) {
    if (t.includes(` ${c} `)) {
      const canonical = canonicalCity(c);
      const knownCity = knownCities.find((k) => canonicalCity(k) === canonical);
      const city = knownCity ?? displayCity(canonical);
      const country = countryForCity(canonical);
      return { online: false, city, ...(country ? { country } : {}) };
    }
  }

  const countryCandidates = [...Object.keys(COUNTRY_ALIASES), ...knownCountries]
    .map(clean)
    .filter((c) => c.length > 1)
    .sort((a, b) => b.length - a.length);
  for (const c of countryCandidates) {
    if (t.includes(` ${c} `)) {
      const canonical = canonicalCountry(c);
      const country =
        knownCountries.find((k) => canonicalCountry(k) === canonical) ??
        COUNTRIES.find((k) => canonicalCountry(k) === canonical) ??
        titleCase(canonical);
      return { online: false, country };
    }
  }
  return null;
}
